import React, { useState, useEffect, useRef, useMemo } from "react";

// Hook para evitar filtrados excesivos mientras el usuario escribe rápido
function useDebounced(v, delay = 250) {
  const [val, setVal] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setVal(v), delay);
    return () => clearTimeout(t);
  }, [v, delay]);
  return val;
}

export default function ComboMultiSelectCertificados({
  label = "Certificados",
  items = [],       // Array de objetos { id, numero, ... }
  loading = false,
  selected = [],    // Array de IDs seleccionados [1, 2, 5]
  onChange = () => {},
}) {
  const boxRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const deb = useDebounced(input, 250);

  // 1. OPTIMIZACIÓN LÓGICA: Filtrar por 'numero'
  const filtered = useMemo(() => {
    const q = deb.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) =>
      // Asumimos que la propiedad visual es 'numero'
      String(c.numero ?? "").toLowerCase().includes(q)
    );
  }, [items, deb]);

  // 2. OPTIMIZACIÓN VISUAL: Renderizado parcial
  const LIMIT = 50;
  const visibleItems = filtered.slice(0, LIMIT);
  const hiddenCount = filtered.length - visibleItems.length;

  const toggle = (id) => {
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];
    onChange(next);
  };

  const marcarTodas = () => {
    const all = filtered.map((c) => c.id);
    // Unimos los seleccionados previos con los nuevos filtrados (evitando duplicados)
    onChange(Array.from(new Set([...selected, ...all])));
  };

  const desmarcarTodas = () => {
    // Quitamos de la selección actual aquellos que están en la lista filtrada
    onChange(selected.filter((id) => !filtered.some((c) => c.id === id)));
  };

  const labelText =
    selected.length === 0
      ? "Seleccionar certificados"
      : selected.length === 1
      ? "1 certificado seleccionado"
      : `${selected.length} seleccionados`;

  // Cierra el menú si se hace clic fuera
  useEffect(() => {
    const onDoc = (ev) => {
      if (boxRef.current && !boxRef.current.contains(ev.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={boxRef} className="relative w-full">
      {label && <label className="label-strong">{label}</label>}

      {/* BOTÓN PRINCIPAL */}
      <button
        type="button"
        className="input w-full flex items-center text-left pl-3 pr-2 bg-white"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="truncate text-gray-700">{labelText}</span>
        <span className="ml-auto text-gray-500">▾</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-80 flex flex-col overflow-hidden">
          
          {/* CABECERA DE FILTRO */}
          <div className="p-2 border-b bg-gray-50 flex flex-wrap items-center gap-2 shrink-0 z-10">
            <input
              placeholder="Buscar N° Certificado..."
              className="flex-1 border rounded px-2 py-1 text-sm"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              autoFocus
            />
            <div className="flex gap-3 px-1">
                <button
                type="button"
                onClick={marcarTodas}
                className="text-green-700 text-xs font-semibold hover:underline"
                title="Marcar todos los resultados filtrados"
                >
                ✓ Todos
                </button>
                <button
                type="button"
                onClick={desmarcarTodas}
                className="text-red-700 text-xs font-semibold hover:underline"
                title="Desmarcar los resultados filtrados"
                >
                ✗ Ninguno
                </button>
            </div>
          </div>

          {/* LISTA CON SCROLL */}
          <div className="flex-1 overflow-auto">
            {!loading && visibleItems.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500 italic">
                Sin resultados
              </div>
            )}

            {loading && (
              <div className="px-3 py-2 text-sm text-gray-500 italic">Cargando datos...</div>
            )}

            {!loading &&
              visibleItems.map((c) => {
                const checked = selected.includes(c.id);
                return (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer transition-colors duration-150"
                  >
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      checked={checked}
                      onChange={() => toggle(c.id)}
                    />
                    <span className="truncate text-sm text-gray-700">
                        {/* Aquí mostramos el NUMERO del certificado */}
                        {c.numero}
                    </span>
                  </label>
                );
              })}

            {/* Aviso de virtualización simple */}
            {!loading && hiddenCount > 0 && (
              <div className="px-3 py-2 text-xs text-gray-400 italic text-center border-t border-gray-100">
                ...y {hiddenCount} registros más. Filtra para encontrarlos.
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="p-2 border-t text-right shrink-0 bg-white">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs font-bold text-gray-500 hover:text-gray-800 uppercase tracking-wide"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}